// slice 02.1 · paso b.6k — nodo "texto" del canvas de roadmap (`ComponentTaskFlow.jsx`): 4
// sub-variantes por flag (`texto-simple`, `texto-inverso`, `post-it`, `texto-enriquecido`) en un
// solo componente, igual que en el original. El de mayor riesgo de la extracción (más líneas,
// más callbacks). El container sigue siendo el único dueño del trío
// `setFlowNodes`+`fetch`+`toast` (pasado como `onDelete`/`onColorChange`/`onFontSizeChange`/
// `onLabelChange`/`onUrlChange`) y del estado de "qué nodo se está editando ahora mismo"
// (`isEditing`/`isEditingUrl`, un solo editor activo a la vez entre todos los nodos de texto —
// por eso vive en el container, no en cada hoja).

import { Trash2, Link } from 'lucide-react';

const POSTIT_PALETTE = {
    'yellow': '#fef08a', 'pink': '#fbcfe8', 'blue': '#bfdbfe', 'green': '#bbf7d0',
    'purple': '#e9d5ff', 'red': '#fecaca', 'white': '#ffffff', 'lightblue': '#bae6fd'
};
const TEXT_COLORS = ['#ffffff', '#000000', '#ef4444', '#3b82f6', '#22c55e', '#eab308', '#a855f7', '#ec4899'];
const TEXT_SIZES = [12, 14, 16, 20, 24, 32, 48, 64];

export default function FlowTextNode({ node, x, y, isMoving, isSelected, scale, isEditing, isEditingUrl, onMoveStart, onResizeStart, onDelete, onColorChange, onFontSizeChange, onLabelChange, onUrlChange, onStartEdit, onToggleEditUrl, onCloseEditUrl }) {
    const isPostit = node.sub === 'post-it';
    const isEnriquecido = node.sub === 'texto-enriquecido';
    const isTextoInverso = node.sub === 'texto-inverso';
    const w = node.w || 200;
    const h = isPostit ? (node.h || 200) : 'auto';

    let bgColor = 'transparent';
    let textColor = node.colorToken || '#ffffff';
    let border = 'none';
    let shadow = 'none';
    let borderRadius = '0';
    let padding = '0';

    if (isPostit) {
        bgColor = POSTIT_PALETTE[node.colorToken] || POSTIT_PALETTE['yellow'];
        textColor = '#222';
        border = '1px solid rgba(0,0,0,0.1)';
        shadow = '2px 4px 10px rgba(0,0,0,0.3)';
        borderRadius = '2px';
        padding = '12px';
    } else if (isEnriquecido) {
        bgColor = POSTIT_PALETTE[node.colorToken] || POSTIT_PALETTE['yellow'];
        textColor = '#222';
        border = '2px solid rgba(0,0,0,0.1)';
        shadow = '2px 4px 10px rgba(0,0,0,0.3)';
        borderRadius = '22px';
        padding = '8px 16px';
    }

    const dynamicFontSize = (isPostit || isEnriquecido) ? (w / 200) * 16 : (node.fontSize || 16);

    return (
        <div className="roadmap-task-node" style={{ ...(isSelected ? { outline: '2px solid #0d99ff', outlineOffset: '2px' } : {}), position: 'absolute', left: x, top: y, zIndex: isMoving ? 50 : 25, width: isTextoInverso ? (w * scale) + 'px' : w, height: isTextoInverso ? (h === 'auto' ? 'auto' : (h * scale) + 'px') : h, transform: isTextoInverso ? `scale(${1 / scale})` : 'none', transformOrigin: 'top left', minHeight: '30px', background: bgColor, border: border, boxShadow: shadow, borderRadius: borderRadius, padding: padding, userSelect: 'none', touchAction: 'none', cursor: !isEditing ? (isMoving ? 'grabbing' : 'grab') : 'default' }} onPointerDown={e => {
            if (!isEditing) onMoveStart(e);
        }} onMouseDown={e => e.stopPropagation()} onTouchStart={e => e.stopPropagation()} onClick={() => {
            if (isEnriquecido && node.url && !isEditing && !isEditingUrl) {
                window.open(node.url.startsWith('http') ? node.url : `https://${node.url}`, '_blank');
            }
        }} onDoubleClick={(e) => { e.stopPropagation(); onStartEdit(); }}>

            {/* Tools Hover Menu (Left) */}
            <div className="task-drag-handle" style={{ position: 'absolute', left: '-35px', top: '0', width: '35px', height: '100%', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '8px', gap: '8px', pointerEvents: 'none' }}>
                {/* Delete */}
                <button onPointerDown={e => e.stopPropagation()} onClick={e => {
                    e.stopPropagation();
                    onDelete();
                }} style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.4)', color: '#ef4444', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px', pointerEvents: 'auto' }}>
                    <Trash2 size={12} />
                </button>

                {/* Link editor toggle */}
                {isEnriquecido && (
                    <button onPointerDown={e => e.stopPropagation()} onClick={e => {
                        e.stopPropagation();
                        onToggleEditUrl();
                    }} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px', pointerEvents: 'auto' }}>
                        <Link size={12} />
                    </button>
                )}

                {/* Color Picker */}
                <div style={{ position: 'relative', pointerEvents: 'auto' }}>
                    <button onPointerDown={e => e.stopPropagation()} onClick={e => {
                        e.stopPropagation();
                        const menu = e.currentTarget.nextElementSibling;
                        menu.style.display = menu.style.display === 'none' ? 'flex' : 'none';
                    }} style={{ background: isPostit ? bgColor : textColor, width: '18px', height: '18px', borderRadius: '50%', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.4)', padding: 0 }} />

                    <div style={{ display: 'none', position: 'absolute', left: '-120px', top: '-10px', background: '#222', border: '1px solid #333', borderRadius: '8px', padding: '4px', gap: '4px', flexWrap: 'wrap', width: '110px', boxShadow: '0 2px 8px rgba(0,0,0,0.5)', zIndex: 100 }}>
                        {(isPostit ? Object.entries(POSTIT_PALETTE) : TEXT_COLORS.map(c => [c, { color: c }])).map(([key, p]) => {
                            const colorVal = isPostit ? key : p.color;
                            const displayColor = isPostit ? POSTIT_PALETTE[key] : p.color;
                            return (
                                <button key={colorVal} onPointerDown={e => e.stopPropagation()} onClick={e => {
                                    e.stopPropagation();
                                    e.currentTarget.parentElement.style.display = 'none';
                                    onColorChange(colorVal);
                                }} style={{ width: '16px', height: '16px', borderRadius: '50%', background: displayColor, border: colorVal === node.colorToken ? '2px solid #000' : 'none', cursor: 'pointer', padding: 0 }} />
                            );
                        })}
                    </div>
                </div>

                {/* Size Picker (Only for Texto Simple) */}
                {!isPostit && (
                    <div style={{ position: 'relative', pointerEvents: 'auto' }}>
                        <button onPointerDown={e => e.stopPropagation()} onClick={e => {
                            e.stopPropagation();
                            const menu = e.currentTarget.nextElementSibling;
                            menu.style.display = menu.style.display === 'none' ? 'flex' : 'none';
                        }} style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: '10px', fontWeight: 'bold', width: '22px', height: '18px', borderRadius: '4px', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.4)', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {node.fontSize || 16}
                        </button>

                        <div style={{ display: 'none', position: 'absolute', left: '-50px', top: '-10px', background: '#222', border: '1px solid #333', borderRadius: '8px', padding: '4px', gap: '2px', flexDirection: 'column', width: '40px', boxShadow: '0 2px 8px rgba(0,0,0,0.5)', zIndex: 100 }}>
                            {TEXT_SIZES.map((size) => (
                                <button key={size} onPointerDown={e => e.stopPropagation()} onClick={e => {
                                    e.stopPropagation();
                                    e.currentTarget.parentElement.style.display = 'none';
                                    onFontSizeChange(size);
                                }} style={{ background: size === node.fontSize ? 'rgba(255,255,255,0.2)' : 'transparent', color: '#fff', fontSize: '12px', cursor: 'pointer', border: 'none', padding: '2px', borderRadius: '4px', textAlign: 'center' }}>
                                    {size}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Text Content */}
            <div style={{ width: '100%', height: '100%', pointerEvents: 'auto', display: 'flex', alignItems: isEnriquecido ? 'center' : 'flex-start', justifyContent: isEnriquecido ? 'center' : 'flex-start' }}>
                {isEditing ? (
                    <textarea
                        autoFocus
                        defaultValue={node.label}
                        style={{ width: '100%', height: '100%', minHeight: '20px', background: 'transparent', border: (isPostit || isEnriquecido) ? 'none' : '1px dashed rgba(255,255,255,0.4)', color: textColor, fontSize: dynamicFontSize + 'px', fontFamily: isPostit ? 'Arial, Helvetica, sans-serif' : 'inherit', resize: 'none', outline: 'none', overflow: 'hidden', textAlign: isEnriquecido ? 'center' : 'left' }}
                        onPointerDown={e => e.stopPropagation()}
                        onBlur={(e) => {
                            onLabelChange(e.target.value);
                        }}
                        onInput={(e) => {
                            let val = e.target.value;
                            if (val.match(/^-\s/gm)) {
                                let newVal = val.replace(/^-\s/gm, '• ');
                                if (newVal !== val) {
                                    e.target.value = newVal;
                                }
                            }
                            if (!isPostit && !isEnriquecido) {
                                e.target.style.height = 'auto';
                                e.target.style.height = e.target.scrollHeight + 'px';
                            }
                        }}
                    />
                ) : (
                    <div style={{ width: '100%', height: '100%', color: textColor, fontSize: dynamicFontSize + 'px', fontFamily: isPostit ? 'Arial, Helvetica, sans-serif' : 'inherit', whiteSpace: 'pre-wrap', cursor: !isEditing ? (isMoving ? 'grabbing' : 'grab') : 'text', wordBreak: 'break-word', pointerEvents: 'auto', textAlign: isEnriquecido ? 'center' : 'left' }}>
                        {node.label || 'Escribe aquí...'}
                    </div>
                )}
            </div>

            {/* URL Editor */}
            {isEnriquecido && isEditingUrl && (
                <div style={{ position: 'absolute', bottom: '-40px', left: 0, width: '100%', background: '#222', border: '1px solid #444', borderRadius: '4px', padding: '4px', zIndex: 100, pointerEvents: 'auto' }}>
                    <input
                        autoFocus
                        type="text"
                        defaultValue={node.url || ''}
                        placeholder="https://..."
                        onPointerDown={e => e.stopPropagation()}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                onUrlChange(e.target.value);
                            } else if (e.key === 'Escape') {
                                onCloseEditUrl();
                            }
                        }}
                        style={{ width: '100%', background: 'transparent', border: 'none', color: '#fff', fontSize: '12px', outline: 'none' }}
                    />
                </div>
            )}

            {/* Resizer */}
            <div
                className="task-resize-handle"
                onPointerDown={(e) => { e.stopPropagation(); onResizeStart(e); }}
                style={{ position: 'absolute', bottom: '-4px', right: '-4px', width: '14px', height: '14px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '2px', cursor: 'nwse-resize', zIndex: 100, pointerEvents: 'auto' }}
            />
        </div>
    );
}
